import Image from 'next/image';

import { Card } from '@commons/components/Card';
import AiiAPatternImage from '@commons/components/Image/AiiAPattern';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import { asAbsoluteURL } from '@commons/helpers/url';
import { useFormattedDate } from '@commons/hooks/useFormattedDate';
import { Link } from '@commons/i18n/navigation';

import { RESULT_IMAGE_SIZES } from '.';
import { type Hit, useHitTypeTranslation } from '../Hit';
import styles from './index.module.scss';

/**
 * ResultDefault component for the Algolia search block.
 *
 * Renders a card that links to the search result's alias.
 */

const ResultDefault = ({ hit }: { hit: Hit }) => {
  const tHit = useHitTypeTranslation();

  const { type, title, alias, image, image_focal_point, date, custom_date, format } = hit;

  const upperTitle = format ? format : tHit(type, 'label');

  // Timestamp in PHP are in seconds while JS uses milliseconds.
  const timestamp = date ? date * 1000 : undefined;

  // Use the custom date if it is provided, otherwise format the timestamp.
  let formattedDate = useFormattedDate(timestamp);
  formattedDate = custom_date || formattedDate;

  const renderedImage = image ? (
    <Image
      src={asAbsoluteURL(image)}
      alt={title}
      fill
      sizes={RESULT_IMAGE_SIZES}
      style={{ objectPosition: image_focal_point }}
    />
  ) : (
    <AiiAPatternImage alt={title} fill sizes={RESULT_IMAGE_SIZES} />
  );

  return (
    <Link href={alias || '#'} className="w-100">
      <Card hasBorder gap="md" justify="space-between" disabled={!alias} className={styles.result}>
        <Flex gap="xs" direction="column">
          <Card.Overlay className={styles.imgWrapper}>{renderedImage}</Card.Overlay>
          <Text level="label-md" transform="uppercase" marginBottom="none">
            {upperTitle}
          </Text>
          <Card.Title>{title}</Card.Title>
        </Flex>
        {formattedDate && (
          <Text level="body-xs" transform="uppercase" marginBottom="none">
            {formattedDate}
          </Text>
        )}
      </Card>
    </Link>
  );
};

export default ResultDefault;
